import { cookieRef } from '@/@layouts/stores/config'
import { urlBase64ToUint8Array } from '@/@layouts/utils'
import { router } from '@/plugins/2.router'
import { ability } from '@/plugins/casl/ability'
import { $api } from '@/utils/api'
import { defineStore } from 'pinia'

const snackbar = useToast()

export const userAuthState = defineStore({
  id: 'auth',
  state: () => {
    return {
      user: JSON.parse(localStorage.getItem('user')) ?? null,
      tenant: JSON.parse(localStorage.getItem('tenant')) ?? null,
      token: localStorage.getItem('accessToken') ?? null,
      tempToken: sessionStorage.getItem('tempToken') ?? null,
    }
  },
  persist: {
    enabled: true,
    strategies: [
      {
        key: 'authStorage',
        storage: sessionStorage, // You can also use sessionStorage or custom storage
      },
    ],
  },
  getters: {
    isLoggedIn: state => !!state.token,
    isLoggedOut: state => !state.user,
    isAdmin: state => state.user?.role === 'admin' || state.user?.is_super_admin,
    isAnalyst: state => state.user?.role === 'client',
    isUser: state => state.user?.role === 'guest',
    userAbilities: state => state.user?.abilities ?? [],
    userRole: state => state.user?.role ?? null,
    getUserData: state => {
      if (!state.user && state.token) {
        // call action
        return this.fetchUser()
      }
      
      return state.user
    },
  },
  actions: {
    setUser(userData) {
      // check if abilities changed
      this.user = userData
      if (this.user) {
        if(this.user?.is_super_admin){
          this.user.abilities = [...this.user.abilities, {
            subject: "all",
            action: "manage",
          }]
        }
        const selectedUnit = cookieRef('selectedUnit')

        // validate selected unit exists in user units
        if(selectedUnit.value && !this.user.units.find(unit => unit.id === selectedUnit.value)){
          selectedUnit.value = null
        }
        if(!selectedUnit.value && this.user?.units?.length>0){
          cookieRef('selectedUnit').value = this.user.units[0]?.id
        }

        // Old Logic
        // this.user.abilities = this.user.abilities ?? []
        // ability.update(this.user.abilities)

        // Refector abilities
        this.user.abilities = this.user.abilities ?? []
        let abilities = this.user.abilities.map(ability => {
          if (
            ability.subject == "dashboard.widget" ||
            ability.subject == "department_dashboard.widget"
          ) {
            return {
              action: 'view',
              subject: `${ability.subject}.${ability.action}`,
            }
          } else {
            return {
              action: ability.action,
              subject: ability.subject,
            }
          }
        })

        // Store and update abilities
        this.user.abilities = abilities
        ability.update(abilities)
      }
    },
    setTenant(data) {
      this.tenant = data

      // if (data) {
      //   localStorage.setItem('tenant', data)
      //   this.tenant = data
      // } else {
      //   this.tenant = null
      //   localStorage.removeItem('accessToken')
      // }
    },
    setToken(token) {
      if(token){
        localStorage.setItem('accessToken', token)
        this.token = token
      }else{
        this.token =null
        localStorage.removeItem('accessToken')
      }
    },
    setTempToken(token) {
      if(token){
        sessionStorage.setItem('tempToken', token)
        this.tempToken = token
      }else{
        this.tempToken = null
        sessionStorage.removeItem('tempToken')
      }
    },
    async fetchUser() {
      if(this.token){
        await $api('auth/user').then(({ userData, tenant }) => {
          this.setUser(userData)
          this.setTenant(tenant)
        }).catch(error => {
          this.setUser(null)
          this.setTenant(null)
          this.setToken(null)
        })
      }

    },
    async login(credentials) {
      let errors = {
        password: ['Something went wrong'],
      }

      return await $api('/auth/login', {
        method: 'POST',
        body: credentials,
      })
        .then(response => {
          // Check if 2FA is required
          if (response.requires_2fa) {
            // Store temporary token for 2FA verification
            this.setTempToken(response.temp_token)

            return {
              status: true,
              requires2FA: true,
              twoFactorMethod: response.method,
              maskedTarget: response.masked_target,
              message: response.message,
            }
          }

          // Normal login flow
          const { accessToken } = response

          this.setToken(accessToken)

          return { status: true, requires2FA: false }
        })
        .catch(error => {
          if(error.message){
            errors.password = [error.data?.message ?? error.message]
          }
          console.error('login error', error)

          return { status: false, errors }
        })

    },
    async verify2FA(code, recoveryCode = null) {
      if (!this.tempToken) {
        return { status: false, message: 'No pending 2FA verification' }
      }

      // Temporarily set the temp token as the auth token for this request
      const originalToken = this.token
      
      this.setToken(this.tempToken)

      const payload = {}

      // If using recovery code, only send recovery_code, not regular code
      if (recoveryCode) {
        payload.recovery_code = recoveryCode
      } else {
        payload.code = code
      }

      return await $api('/auth/login-2fa/verify', {
        method: 'POST',
        body: payload,
      })
        .then(({ accessToken }) => {
          // Clear temp token
          this.setTempToken(null)

          // Set real token
          this.setToken(accessToken)

          // Fetch user data with the new token
          return this.fetchUser().then(() => {
            return { status: true }
          })
        })
        .catch(error => {
          // Restore original token on error
          this.setToken(originalToken)

          return {
            status: false,
            message: error.message || 'Invalid verification code',
            is_locked: error.data?.is_locked || false,
            lockout_time_remaining: error.data?.lockout_time_remaining || 0,
            failed_attempts: error.data?.failed_attempts || 0,
          }
        })
    },
    async resend2FA() {
      if (!this.tempToken) {
        return { status: false, message: 'No pending 2FA verification' }
      }

      // Temporarily set the temp token as the auth token for this request
      const originalToken = this.token

      this.setToken(this.tempToken)

      return await $api('/auth/login-2fa/resend', {
        method: 'POST',
      })
        .then(() => {
          // Restore original token
          this.setToken(originalToken)
          
          return { status: true, message: 'Verification code sent successfully' }
        })
        .catch(error => {
          // Restore original token
          this.setToken(originalToken)
          
          return { status: false, message: error.message || 'Failed to resend verification code' }
        })
    },
    async switchUnit(unitId) {
      return await $api('/auth/switch-unit', {
        method: 'POST',
        body: { unitId },
      })
        .then(({ userData, tenant, accessToken }) => {
          this.setUser(userData)
          this.setTenant(tenant)
          this.setToken(accessToken)
          
          return { status: true }
        })
        .catch(error => {
          return { status: false }
        })
    },
    async subscribeToNotifications() {

      try{

        if (!('serviceWorker' in navigator)) {
          console.log('Service worker not supported')
          
          return
        }
        const registration = await navigator.serviceWorker.ready
  
        const subscription = await registration.pushManager.getSubscription()
        if (!subscription) {
          try {
            const subscription = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC_KEY),
            })
          
            console.log('Subscribing to notifications:', subscription)
            
            return $api('/auth/subscribe', {
              method: 'POST',
              body: subscription,
            })
          } catch (error) {
            console.error('Error during subscription:', error)
            throw error
          }
        }
      }
      catch(error){
        console.log('ServiceWorker registration failed:', error)
      }
    },
    async unsubscribeFromNotifications() {
      if (!('serviceWorker' in navigator)) {
        console.log('Service worker not supported')
        
        return
      }
      const registration = await navigator.serviceWorker.ready

      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        console.log('Unsubscribing from notifications')
        
        return subscription.unsubscribe().then(() => {
          return $api('/auth/unsubscribe', {
            method: 'POST',
            body: subscription,
          })
        })
      }
    }

    ,
    async logout() {
      this.unsubscribeFromNotifications()
      try{

        $api('/auth/logout', {
          method: 'GET',
        })
      }catch(error){
        console.error('logout error', error)
      }finally{
        this.setUser(null)
        this.setTenant(null)
        this.setToken(null)
        localStorage.removeItem('user')
        localStorage.removeItem('tenant')
        router.push({ name: 'app-login' })
      }
    },

    async forgotPassword(userData){
      $api('auth/forgot-password', {
        method: 'POST',
        body: userData,
      }).then(response => {
        if(!response.errors)
        {
          snackbar.success('We have emailed your password reset link.', 'success')
          router.push('/app/login')
        }
        
        return response
      })
        
    },

    async resetPassword(formData){
      $api('auth/reset-password', {
        method: 'POST',
        body: formData,
      }).then(response => {
        if(!response.errors)
        {
          snackbar.success('Your password has been reset.', 'success')
          router.push('/app/login')
        }
        
        return response
      })
    },
  },
  computed: {
    user: state => state.user,
    tenant: state => state.tenant,
    token: state => state.token,
  },
})
